
import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, Chip, Box, Typography, Popover, Tooltip } from "@mui/material";
import { Add, CurrencyExchangeOutlined } from "@mui/icons-material";
import { useTablePagination, useAppMeta, useAuth } from "hooks";

import AppTable from "components/Shared/AppTable";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import HighlightedText from "components/Shared/HighlightedText";
import { useLazyGetCocCollectionListForVendorQuery } from "store/services/cocCollectionApi";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { BaseTabs } from "components/Shared/BaseTabs";
import TableFilter from "components/Shared/AppTable/TableFilter";

const ShipmentsCell = ({ shipments = [], keyword }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedShipments, setSelectedShipments] = React.useState([]);

  const visibleShipments = shipments.slice(0, 2);
  const remainingCount = shipments.length - visibleShipments.length;

  const handleChipClick = (event, allShipments) => {
    setAnchorEl(event.currentTarget);
    setSelectedShipments(allShipments);
  };

  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);

  return shipments.length > 0 ? (
    <>
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
        {visibleShipments.map((shipment, index) => {
          const shiId = shipment?.shipmentId
            ?.replace(/(.{4})/g, "$1-")
            .slice(0, -1);
          return (
            <span key={index} style={{ fontSize: "0.9rem" }}>
              <HighlightedText text={shiId} highlight={keyword} />
              {index !== visibleShipments.length - 1 && ","}
            </span>
          );
        })}

        {remainingCount > 0 && (
          <Tooltip title="Click to view all Shipment IDs">
            <Chip
              label={`+${remainingCount}`}
              size="small"
              sx={{
                ml: 0.5,
                height: 18,
                fontSize: "10px",
                bgcolor: "#e0e0e0",
                color: "#333",
                cursor: "pointer",
              }}
              onClick={(e) => handleChipClick(e, shipments)}
            />
          </Tooltip>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box p={2} minWidth={100}>
          {selectedShipments.map((shipment, index) => (
            <Typography key={index} variant="body2" sx={{ wordBreak: "break-word" }}>
              <HighlightedText text={shipment.shipmentId} highlight={keyword} />
            </Typography>
          ))}
        </Box>
      </Popover>
    </>
  ) : (
    "N/A"
  );
};

const CocCollectionListScreen = () => {
  const [searchParams] = useSearchParams();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const { isVendor } = useAuth();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Counter Collection" }];

  const CocCollectionListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Fee Item",
      selector: (row) => row?.priceItem?.feeItem,
      cell: (row) => (
        <div title={row?.priceItem?.feeItem} className="line-clamp-2">
          {row?.priceItem?.feeItem || "N/A"}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Charge Type",
      selector: (row) => row?.priceItem?.chargeType,
      cell: (row) => (
        <div title={row?.priceItem?.chargeType} className="line-clamp-2">
          {row?.priceItem?.chargeType || "N/A"}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Charge",
      selector: (row) => row?.priceItem?.charge,
      cell: (row) => {
        const isRefunded =
          row?.priceItem?.paymentStatus === "Refunded" ||
          (row?.priceItem?.charge != null && Number(row?.priceItem?.charge) < 0);
        const chargeNum = Number(row?.priceItem?.charge || 0);
        if (row?.priceItem?.charge == null) return "N/A";

        if (isRefunded) {
          const absVal = Math.abs(chargeNum).toFixed(2);
          return (
            <Tooltip title="Refunded charge: reversal / deduction">
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  bgcolor: "#FEF2F2",
                  color: "#DC2626",
                  border: "1px solid #FECACA",
                  borderRadius: "6px",
                  px: 1,
                  py: 0.3,
                  fontWeight: 700,
                  fontSize: "0.85rem",
                }}
              >
                <span>-Rs. {absVal}</span>
              </Box>
            </Tooltip>
          );
        }

        return (
          <Typography
            component="span"
            variant="body2"
            sx={{
              color: "text.primary",
              fontWeight: 500,
            }}
          >
            Rs. {chargeNum.toFixed(2)}
          </Typography>
        );
      },
      minWidth: "150px",
    },
    {
      name: "Payment Status",
      selector: (row) => row?.priceItem?.paymentStatus,
      cell: (row) => {
        const status = row?.priceItem?.paymentStatus || "N/A";

        if (status === "Refunded") {
          return (
            <Chip
              icon={
                <CurrencyExchangeOutlined
                  sx={{ fontSize: "14px !important", color: "#DC2626 !important" }}
                />
              }
              label="Refunded"
              size="small"
              sx={{
                bgcolor: "#FEF2F2",
                color: "#DC2626",
                border: "1px solid #FECACA",
                fontWeight: 700,
                fontSize: "0.75rem",
                height: "24px",
              }}
            />
          );
        }

        if (status === "Paid") {
          return (
            <Chip
              label="Paid"
              size="small"
              sx={{
                bgcolor: "#F0FDF4",
                color: "#16A34A",
                border: "1px solid #BBF7D0",
                fontWeight: 600,
                fontSize: "0.75rem",
                height: "24px",
              }}
            />
          );
        }

        if (status === "Not Paid") {
          return (
            <Chip
              label="Not Paid"
              size="small"
              sx={{
                bgcolor: "#FFFBEB",
                color: "#D97706",
                border: "1px solid #FDE68A",
                fontWeight: 600,
                fontSize: "0.75rem",
                height: "24px",
              }}
            />
          );
        }

        if (status === "Collected") {
          return (
            <Chip
              label="Collected"
              size="small"
              sx={{
                bgcolor: "#F0FDFA",
                color: "#0D9488",
                border: "1px solid #99F6E4",
                fontWeight: 600,
                fontSize: "0.75rem",
                height: "24px",
              }}
            />
          );
        }

        return (
          <Chip
            label={status}
            size="small"
            sx={{
              bgcolor: "#F5F5F5",
              color: "#616161",
              fontWeight: 500,
              fontSize: "0.75rem",
              height: "24px",
            }}
          />
        );
      },
      minWidth: "150px",
    },
    {
      name: "Shipment ID",
      selector: (row) => row?.shipment?.shipmentId,
      cell: (row) => (
        <ShipmentsCell
          shipments={row?.shipment ? [{ shipmentId: row.shipment.shipmentId }] : []}
          keyword={keyword}
        />
      ),
      minWidth: "200px",
    },
    {
      name: "Shipment Status",
      selector: (row) => row?.shipment?.status,
      cell: (row) => (
        <div title={row?.shipment?.status} className="line-clamp-2">
          {row?.shipment?.status || "N/A"}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Shipment Created Date",
      selector: (row) => row?.shipment?.createdDate,
      cell: (row) => <AppTableDateTime date={row?.shipment?.createdDate} />,
      minWidth: "200px",
    },
  ];

  const [
    fetchCocCollection,
    {
      data: CocCollectionList,
      isLoading: loadingCocCollection,
      isFetching: fetchingCocCollection,
      error,
    },
  ] = useLazyGetCocCollectionListForVendorQuery();

  React.useEffect(() => {
    fetchCocCollection({
      active: activeTab === "Active" ? "true" : "false",
      keywords: keyword,
      page: currentPage,
      limit: perPage,
    });
  }, [activeTab, fetchCocCollection, keyword, currentPage, perPage]);

  useAppMeta(`${activeTab} COC Collection`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        {!isVendor && (
          <BaseTabs tabs={CocCollectionListTabs} activeTab={activeTab} />
        )}
        <TableFilter searchLabel="Shipment ID" initialFilters={3}>
          <div className="font-medium"></div>
        </TableFilter>

        <AppTable
          columns={columns}
          data={Object.values(CocCollectionList?.data || {})}
          paginationServer
          paginationPerPage={perPage}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          progressPending={loadingCocCollection || fetchingCocCollection}
          paginationTotalRows={CocCollectionList?.totalItems ?? 0}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

const PageActions = () => {
  const { isVendor } = useAuth();

  return (
    <>
      {!isVendor && (
        <div
          style={{
            display: "flex",
            alignContent: "center",
            justifyContent: "space-between",
          }}
        >
          <Link to="/app/crm/coc-collection/create">
            <Button startIcon={<Add />} variant="contained">
              New Counter Collection
            </Button>
          </Link>
        </div>
      )}
    </>
  );
};

export default CocCollectionListScreen;